import React, { useCallback, useEffect, useMemo, useState } from "react";
import { localizationClient } from "../../api/v2/localizationClient";
import { AuditEvent } from "../../api/v2/types";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { parseApiError } from "../../api/http/errors";
import SectionState from "../shared/SectionState";
import InlineHint from "../shared/InlineHint";
import HelpHoverCard from "../shared/HelpHoverCard";
import { Spinner } from "../../components/ui/spinner";
import AnalyticsDashboard from "../analytics/components/AnalyticsDashboard";

interface Props {
  owner: string;
  repo: string;
  workspaceKey: string;
}

const filterEvents = (events: AuditEvent[], query: string) => {
  const lower = query.trim().toLowerCase();
  if (!lower) return events;
  return events.filter((event) => {
    const source = [event.action, event.status, event.requestId, event.operationId].join(" ");
    return source.toLowerCase().includes(lower);
  });
};

const renderEvent = (event: AuditEvent) => (
  <div key={`${event.id}-${event.timestamp}`} className="border rounded p-3 text-sm">
    <p>
      {event.timestamp} - {event.action} - {event.status}
    </p>
    <p className="text-xs text-gray-500">
      user={event.actorUserId || "n/a"} requestId={event.requestId || "n/a"} operationId=
      {event.operationId || "n/a"}
    </p>
  </div>
);

const ActivityTab = ({ owner, repo, workspaceKey }: Props) => {
  const [logs, setLogs] = useState<AuditEvent[]>([]);
  const [filter, setFilter] = useState("");
  const [errorText, setErrorText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    setErrorText("");
    setIsLoading(true);
    try {
      const logData = await localizationClient.getObservabilityLogs(owner, repo);
      setLogs(logData.events);
    } catch (error) {
      setErrorText(parseApiError(error).message);
    } finally {
      setIsLoading(false);
    }
  }, [owner, repo]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredLogs = useMemo(() => filterEvents(logs, filter), [logs, filter]);
  const visibleCount = filteredLogs.length;

  return (
    <div className="space-y-4">
      <HelpHoverCard
        title="Status and logs"
        description="This tab shows repository analytics dashboard first, then observability logs for detailed event tracing."
      />
      <p className="text-xs text-muted-foreground">Workspace scope: {workspaceKey}</p>
      <AnalyticsDashboard owner={owner} repo={repo} workspaceKey={workspaceKey} />
      <div className="flex gap-2">
        <Input
          placeholder="Filter by action/status/requestId/operationId/entity"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <Button variant="outline" onClick={load}>
          {isLoading ? <Spinner className="mr-1 h-4 w-4" /> : null}
          Refresh
        </Button>
        <Button variant="outline" onClick={() => setFilter("")} disabled={!filter}>
          Clear
        </Button>
        <InlineHint text="Filtering supports action, status, requestId and operationId fields." />
      </div>
      <p className="text-xs text-muted-foreground">Visible events: {visibleCount}</p>
      <SectionState
        isLoading={isLoading}
        errorText={errorText}
        isEmpty={visibleCount === 0}
        emptyText="No log events for current filter."
      >
        <section className="space-y-2">
          <h3 className="font-semibold">Observability logs</h3>
          {filteredLogs.map(renderEvent)}
        </section>
      </SectionState>
    </div>
  );
};

export default ActivityTab;
